import { api } from '@/api'
import { UserProfile } from '@/features/profile/api/profile'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosRequestConfig } from 'axios'
import { Mission } from './models'
import { useRetryMissionList } from './useRetryMissionList'

export { useCountries, useLanguages } from '../../profile/api/dictionaries'
export type { SelectOption } from '../../profile/api/dictionaries'

export type UseMissionsListOptions = {
  platform?: 'web' | 'mobile'
}

export const useMissionsList = ({ platform = 'web' }: UseMissionsListOptions = {}) => {
  return useQuery({
    queryKey: ['MISSION_LIST', platform],
    queryFn: () => {
      return api.get<Mission[]>(`/mission?platform=${platform}`)
    },
    staleTime: 50_000,
    placeholderData: keepPreviousData
  })
}

export const useMission = (missionId: string | undefined) => {
  return useQuery({
    queryKey: ['MISSION', missionId],
    queryFn: () => api.get<Mission>(`/mission/${missionId}`),
    enabled: !!missionId
  })
}
export const usePublicMissionsList = ({ platform = 'web' }: UseMissionsListOptions = {}) => {
  return useQuery({
    queryKey: ['PUBLIC_MISSION_LIST', platform],
    queryFn: () => {
      return api.get<Mission[]>(`/mission/public?platform=${platform}`)
    },
    select: (missions) =>
      missions.map((mission) => ({
        ...mission,
        availability: { available: true, reason: undefined },
        view: { latestSubmissionStatus: null, nextSubmissionAvailable: true }
      })),
    staleTime: 50_000,
    placeholderData: keepPreviousData
  })
}

export const usePublicMission = (missionId: string | undefined) => {
  return useQuery({
    queryKey: ['PUBLIC_MISSION', missionId],
    queryFn: () => api.get<Mission>(`/mission/public/${missionId}`),
    select: (mission) => ({
      ...mission,
      availability: { available: true, reason: undefined },
      view: { latestSubmissionStatus: null, nextSubmissionAvailable: true }
    }),
    enabled: !!missionId
  })
}

export const useUploadSubmission = () => {
  const queryClient = useQueryClient()
  const { startRetrying } = useRetryMissionList()

  return useMutation({
    mutationFn: ({ missionId, data }: { missionId: string; data: FormData }) =>
      api.post(`/mission/${missionId}/submit/file`, data),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['MISSION_LIST'] })
      await queryClient.refetchQueries({ queryKey: ['MISSION'] })
      startRetrying()
    }
  })
}

export const useSubmitMissionResult = () => {
  const queryClient = useQueryClient()
  const { startRetrying } = useRetryMissionList()

  return useMutation({
    mutationFn: ({ missionId, submission }: { missionId: string; submission?: Record<string, unknown> }) =>
      api.post(`/mission/${missionId}/submit`, { submission: submission ?? {} }),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['MISSION_LIST'] })
      await queryClient.refetchQueries({ queryKey: ['MISSION'] })
      startRetrying()
    }
  })
}

export const useSubmitMissionProfile = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      missionId,
      data,
      turnstileToken
    }: {
      missionId: string
      data: UserProfile
      turnstileToken?: string
    }) => {
      const config: AxiosRequestConfig | undefined = turnstileToken
        ? { headers: { 'X-Turnstile-Token': turnstileToken } }
        : undefined
      return api.post(`/mission/${missionId}/submit/profile`, data, config)
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['MISSION_LIST'] })
      await queryClient.refetchQueries({ queryKey: ['MISSION'] })
    }
  })
}
